import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { PlusIcon } from 'lucide-react'
import { useState } from 'react'
import { AddSiteDialog } from '@/components/add-site-dialog'
import { useDateWindow } from '@/components/date-range-picker'
import { EmptyState } from '@/components/empty-state'
import { EngineFilterControl, parseEngineFilter } from '@/components/engine-filter'
import { EstateChart } from '@/components/estate-chart'
import { Panel } from '@/components/panel'
import { MetricTiles } from '@/components/performance-panel'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { bandsFor, useEstate } from '@/lib/estate'
import type { MetricKey } from '@/lib/metrics'
import { DEFAULT_DATE_RANGE, sitesQuery } from '@/lib/queries'
import type { EngineFilter } from '@/lib/site-data'
import { EstateTable } from './-estate-table'
import { OutreachSummary } from './-outreach-summary'

export const Route = createFileRoute('/_app/$org/')({
  validateSearch: (search: Record<string, unknown>): { engine?: EngineFilter } => {
    const engine = parseEngineFilter(search.engine)
    return engine ? { engine } : {}
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(sitesQuery),
  component: OverviewPage,
})

/**
 * The workspace as one number, then as its parts. Someone with nine sites
 * opens this to find out whether the estate moved and which site moved it —
 * the chart answers the first from its height and the second from its bands.
 */
function OverviewPage() {
  const { org } = Route.useParams()
  const { range = DEFAULT_DATE_RANGE, engine = 'all' } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [metric, setMetric] = useState<MetricKey>('clicks')
  // Shared by the chart and the table, so pointing at a row lights its band.
  const [highlight, setHighlight] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)

  const { data: siteList } = useQuery(sitesQuery)
  const { data, isPending } = useEstate(range, engine)
  useDateWindow(data?.range)

  const sites = data?.sites ?? []
  const bands = bandsFor(sites, expanded)
  const connected = sites.some((site) => site.searchConsole !== 'not-connected' || site.bing)
  const read = data?.engine ?? 'all'
  const knownEmpty = (siteList?.sites.length ?? 0) === 0 && !isPending

  return (
    <div className="space-y-4">
      <h1 className="sr-only">Overview</h1>
      {/* No action row: the window is in the top bar and adding a site belongs
          to the page that lists them. A control repeated on the section above
          it is one the reader has to decide about twice. */}
      <OutreachSummary org={org} range={range} />
      {knownEmpty ? (
        <Card>
          <EmptyState
            title="No sites yet"
            description="Add the domain you want to track. Connecting a source comes next, and is what makes this page worth opening."
            action={
              <AddSiteDialog
                trigger={
                  <Button size="sm">
                    <PlusIcon />
                    Add site
                  </Button>
                }
              />
            }
          />
        </Card>
      ) : isPending || !data ? (
        <OverviewSkeleton />
      ) : !connected ? (
        <Card>
          <EmptyState
            title="Nothing connected yet"
            description={`No search data has been collected for any of your ${sites.length} sites. Connect the account that owns them and the last 16 months start filling in.`}
            action={
              <Button asChild size="sm">
                <Link to="/$org/settings/connections" params={{ org }}>
                  Connect a source
                </Link>
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="space-y-4">
          <Panel
            title="Search"
            flush
            actions={
              <EngineFilterControl
                size="sm"
                value={read}
                engines={data.engines}
                onChange={(next) =>
                  navigate({
                    search: (prev) => ({ ...prev, engine: next === 'all' ? undefined : next }),
                    replace: true,
                  })
                }
              />
            }
          >
            <MetricTiles
              totals={data.totals}
              split={data.split}
              metric={metric}
              onSelect={setMetric}
            />
            <div className="border-t border-border px-4 pt-5 pb-4">
              <EstateChart
                buckets={data.buckets}
                bands={bands}
                metric={metric}
                bucketDays={data.bucketDays}
                pending={data.pending}
                highlight={highlight}
                onHighlight={setHighlight}
                expanded={expanded}
                onExpand={() => setExpanded((open) => !open)}
              />
            </div>
          </Panel>

          <EstateTable
            org={org}
            sites={sites}
            bands={bands}
            metric={metric}
            engine={read}
            range={range}
            highlight={highlight}
            onHighlight={setHighlight}
          />
        </div>
      )}
    </div>
  )
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="rounded-lg border border-border bg-card">{children}</div>
}

function OverviewSkeleton() {
  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="h-9 border-b border-border bg-secondary/40" />
        <div className="grid grid-cols-2 divide-x divide-y divide-border lg:grid-cols-4 lg:divide-y-0">
          {[0, 1, 2, 3].map((tile) => (
            <div key={tile} className="space-y-3 p-4">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
        <div className="border-t border-border p-4">
          <Skeleton className="h-[240px] w-full" />
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="h-9 border-b border-border bg-secondary/40" />
        {[0, 1, 2, 3].map((row) => (
          <div
            key={row}
            className="flex items-center gap-4 border-b border-border px-4 py-3 last:border-b-0"
          >
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-12" />
          </div>
        ))}
      </div>
    </div>
  )
}
